// ---------------------------------------------------------------------------
// THE ROUTE TABLE, AS DATA.
//
// Until now the routes were JSX inside App.tsx, which meant "every screen in
// the product" was a thing you could only establish by reading a file
// carefully. 10-demo-gallery.md §7.2 requires the gallery index to be checked
// against the route table "by comparing the route table against the registry,
// not by hand" — and a hand-maintained list is exactly the check that decayed
// on verify-V3.sh and went unnoticed for two slices.
//
// So the list lives here, in a file with no React in it, and two things consume
// it:
//
//   App.tsx           renders one <Route> per entry, looking the element up in
//                     a Record<ScreenId, ReactNode>. TypeScript makes that map
//                     exhaustive, so a route with no element does not compile.
//   demo/registry.ts  registers gallery states per screen, ALSO as a
//                     Record<ScreenId, …>. Which means DEFINITION OF DONE POINT
//                     9 IS ENFORCED BY THE COMPILER: a slice that adds a screen
//                     and does not register it in the gallery cannot typecheck.
//
// That last sentence is the whole reason this file exists. Point 9 has been
// deferred by build order six times; a script somebody has to remember to run
// would have been the seventh.
// ---------------------------------------------------------------------------

import type { Surface } from "./navigation";

/**
 * Where a screen lives.
 *
 * `public` is not a surface — it is the absence of one. The four token-link
 * screens and sign-in/sign-up render outside any Protected boundary and carry
 * the warm brand palette (11-console-design-system.md §2), which is why they
 * cannot simply be a fourth member of Surface: Surface decides a palette and a
 * density, and these have neither of the application's.
 */
export type ScreenHome = Surface | "public";

/**
 * What kind of thing the screen is, which decides WHICH GALLERY STATES IT OWES.
 *
 * This is not decoration. 10-demo-gallery.md §2 lists five states and says a
 * screen must register "every state it can occupy" — and left at that, every
 * screen would register the one state its author found easy. Naming the kind
 * turns the obligation into arithmetic: a collection owes all five, and the
 * test below can say so.
 */
export type ScreenKind =
  /** Renders a list of rows. Owes all five states — over-capacity included. */
  | "collection"
  /** One record, with detail panels. */
  | "record"
  /** A create or edit form. */
  | "form"
  /** A dashboard or summary. */
  | "overview"
  /** A single message: not found, no access, no connection. */
  | "notice";

export type Screen = {
  /** Stable id. Spelled the same in the element map and in the gallery. */
  id: string;
  home: ScreenHome;
  /**
   * For a surface screen: the path RELATIVE to the surface prefix, exactly as
   * App.tsx's nested <Routes> wants it. `""` is the surface index.
   * For a public screen: the absolute path.
   *
   * `*` is a real entry and not an oversight — a not-found screen is a screen,
   * it is the one every mistyped link reaches, and leaving it out of the
   * gallery is how it ends up being the least-looked-at screen in a product
   * while being one of the most-seen.
   */
  path: string;
  /** Human title. The gallery index shows it; nothing else does. */
  title: string;
  /** Which slice introduced it. The index shows this so gaps are attributable. */
  slice: string;
  kind: ScreenKind;
  /**
   * Concrete values for any `:param` in the path, so the gallery can render the
   * screen at a real URL. Asserted complete by screens.test.ts — a screen with
   * a parameter and no sample cannot be rendered and would silently register a
   * gallery entry that shows an error page.
   */
  params?: Record<string, string>;
  /**
   * A query string the gallery appends, for a screen reached from an emailed
   * link whose token rides in `?token=`. Tour T-5: without it the gallery only
   * ever showed those screens refusing an incomplete link.
   */
  search?: string;
};

// Ids of the fixture records the parameterised screens point at. Spelled here
// rather than imported from web/src/demo so that this file carries no
// dependency on the gallery — the gallery depends on the product, never the
// other way round, or the tree-shaking that keeps the gallery out of a
// production build stops working.
const DEMO_CLIENT = "00000000-0000-4000-8000-c00000000001";
const DEMO_WORKER = "00000000-0000-4000-8000-w00000000001";
const DEMO_EVENT = "00000000-0000-4000-8000-e00000000001";
/** V16. The sample the gallery renders the worker's shift detail against. */
const DEMO_SHIFT = "00000000-0000-4000-8000-a00000000001";
/** Sweep S21b. The stored document and the payment batch the gallery renders. */
const DEMO_DOCUMENT = "00000000-0000-4000-8000-d00000000001";
const DEMO_BATCH = "00000000-0000-4000-8000-b00000000001";
/** Sweep §4 / 0215. The application link the gallery renders the public page against. */
const DEMO_APPLICATION_LINK = "demoApplicationLinkxxxxxxxxxxxxxxxxxxxxxxxx";

export const SCREENS = [
  // ---- public: no session, warm brand palette (§2) -------------------------
  { id: "public.sign-in", home: "public", path: "/sign-in", title: "Sign in", slice: "V3", kind: "form" },
  { id: "public.sign-up", home: "public", path: "/sign-up", title: "Create an account", slice: "V5", kind: "form" },
  { id: "public.accept-invitation", home: "public", path: "/accept-invitation", title: "Accept an invitation", slice: "V5", kind: "form", search: "?token=demoEmailedLinkTokenxxxxxxxxxxxxxxxxxxxxxxxxx" },
  { id: "public.choose-organisation", home: "public", path: "/choose-organisation", title: "Choose an organisation", slice: "V3", kind: "collection" },
  { id: "public.enter", home: "public", path: "/enter", title: "Enter with a link", slice: "V3", kind: "notice", search: "?token=demoEmailedLinkTokenxxxxxxxxxxxxxxxxxxxxxxxxx" },
  { id: "public.oidc-callback", home: "public", path: "/auth/callback", title: "Signing you in", slice: "V35", kind: "notice" },
  { id: "public.reset", home: "public", path: "/reset", title: "Set a new password", slice: "V3", kind: "form", search: "?token=demoEmailedLinkTokenxxxxxxxxxxxxxxxxxxxxxxxxx" },
  { id: "public.verify", home: "public", path: "/verify", title: "Verify an email address", slice: "V3", kind: "notice", search: "?token=demoEmailedLinkTokenxxxxxxxxxxxxxxxxxxxxxxxxx" },
  // SWEEP S7. Every notification email links here; until 3 October it was not-found.
  { id: "public.unsubscribe", home: "public", path: "/unsubscribe", title: "Stop these emails", slice: "V25", kind: "notice", search: "?token=demoEmailedLinkTokenxxxxxxxxxxxxxxxxxxxxxxxxx" },
  // Sweep §4 / 0215. An agency's public application page. The token is the link the
  // agency opened and may revoke; the database decides whether it names anything.
  { id: "public.apply", home: "public", path: "/apply/:token", title: "Apply for work", slice: "Sweep §4", kind: "form", params: { token: DEMO_APPLICATION_LINK } },
  { id: "public.not-found", home: "public", path: "*", title: "Not found", slice: "V4", kind: "notice" },

  // ---- console -------------------------------------------------------------
  { id: "console.home", home: "console", path: "", title: "Overview", slice: "V4", kind: "overview" },
  { id: "console.clients", home: "console", path: "clients", title: "Clients", slice: "V5", kind: "collection" },
  { id: "console.clients.new", home: "console", path: "clients/new", title: "Add a client", slice: "V5", kind: "form" },
  { id: "console.clients.detail", home: "console", path: "clients/:id", title: "One client", slice: "V5", kind: "record", params: { id: DEMO_CLIENT } },
  { id: "console.workers", home: "console", path: "workers", title: "Bench", slice: "V6", kind: "collection" },
  { id: "console.workers.new", home: "console", path: "workers/new", title: "Add to the bench", slice: "V6", kind: "form" },
  { id: "console.workers.detail", home: "console", path: "workers/:id", title: "One bench record", slice: "V6", kind: "record", params: { id: DEMO_WORKER } },
  { id: "console.workers.availability", home: "console", path: "workers/:id/availability", title: "Availability", slice: "V12", kind: "collection", params: { id: DEMO_WORKER } },
  { id: "console.people", home: "console", path: "people", title: "People", slice: "V5", kind: "collection" },
  { id: "console.credential-types", home: "console", path: "credential-types", title: "Credentials", slice: "V7", kind: "collection" },
  // SWEEP S21b. Two reads that existed with no screen: a stored document with its
  // access log (linked from a credential's evidence), and one payment batch line by
  // line (linked from the pay screen's batch table).
  { id: "console.documents.detail", home: "console", path: "documents/:id", title: "One document", slice: "V7", kind: "record", params: { id: DEMO_DOCUMENT } },
  { id: "console.payment-batches.detail", home: "console", path: "payment-batches/:id", title: "One payment batch", slice: "V23", kind: "record", params: { id: DEMO_BATCH } },
  { id: "console.roles", home: "console", path: "roles", title: "Roles", slice: "V8", kind: "collection" },
  { id: "console.configuration", home: "console", path: "configuration", title: "Configuration packs", slice: "V9", kind: "collection" },
  // V10. `events/new` MUST COME BEFORE `events/:id` — the literal "new" is
  // otherwise captured as an id and the create screen becomes a 404. The same
  // hazard the API has with /v1/workers/me, in the same shape.
  { id: "console.events", home: "console", path: "events", title: "Events", slice: "V9", kind: "collection" },
  { id: "console.hub-requests", home: "console", path: "hub-requests", title: "Requests from event.clinic", slice: "V-ETA-2b", kind: "collection" },
  { id: "console.events.new", home: "console", path: "events/new", title: "New event", slice: "V10", kind: "form" },
  { id: "console.events.detail", home: "console", path: "events/:id", title: "Event workspace", slice: "V10", kind: "record", params: { id: DEMO_EVENT } },
  // V11. `requirements/new` MUST COME BEFORE `requirements` is irrelevant here
  // — they differ in depth, not in shape — but `requirements` must come after
  // `events/:id`, which it does. The hazard in this table is a literal segment
  // below a `:param` AT THE SAME DEPTH, which is why `events/new` is above
  // `events/:id` and nothing else needs to move.
  { id: "console.events.requirements", home: "console", path: "events/:id/requirements", title: "Requirements", slice: "V11", kind: "collection", params: { id: DEMO_EVENT } },
  { id: "console.events.requirements.new", home: "console", path: "events/:id/requirements/new", title: "Add a requirement", slice: "V11", kind: "form", params: { id: DEMO_EVENT } },
  // V14. THE CONTROL ROOM, AND IT IS THE SAME SCREEN V13 SHIPPED AS "Crew".
  //
  // The id and the path do not move, and that was the decision rather than the
  // path of least resistance. A board at `events/:id/board` beside a crew list
  // at `events/:id/assignments` would be two screens over one set of bookings —
  // the copy that stops agreeing, which this repository has now been bitten by
  // often enough to name. Everything the list did lives in the board's cell
  // panel; nothing was dropped and there is nowhere for a second answer to come
  // from. `slice` moves to V14 because V14 is the slice that now owns it.
  { id: "console.events.crew", home: "console", path: "events/:id/assignments", title: "Control Room", slice: "V14", kind: "collection", params: { id: DEMO_EVENT } },
  // V20. THE LIVE OPS CONSOLE, at `board` beside the Control Room's `assignments`.
  //
  // TWO SCREENS OVER ONE SET OF BOOKINGS IS THE THING V14's NOTE ABOVE WARNS ABOUT, so the
  // distinction has to be real: the Control Room is where a roster is BUILT and it is a
  // planning surface; this is where the day is WATCHED and it writes nothing at all. They
  // read different questions — one asks who is booked, the other asks who is standing there
  // — and neither can answer the other's. `app.live_board` is the single source for the
  // second question, so there is nowhere for a second answer to come from.
  { id: "console.events.live", home: "console", path: "events/:id/board", title: "Live ops", slice: "V20", kind: "overview", params: { id: DEMO_EVENT } },
  // V21. TIMESHEETS, AT `timesheets` BESIDE THE BOARD AND THE CONTROL ROOM.
  //
  // ONE SCREEN AND NOT TWO. FORWARD-PLAN.md asks for a supervisor screen and an agency
  // screen; they would read the same rows in the same order from the same call, and the
  // only difference is which button is live. V14's note above is exactly this warning,
  // and the server answers the affordance per step rather than the screen guessing from
  // a role name.
  { id: "console.events.timesheets", home: "console", path: "events/:id/timesheets", title: "Timesheets", slice: "V21", kind: "collection", params: { id: DEMO_EVENT } },
  // V22. ★ THE PROOF PACK, at `proof` beside the timesheets it cites.
  //
  // A `collection` rather than a `record`: the thing on the screen is the LIST of packs
  // issued for this event, because §5 makes a correction a new pack rather than an edit —
  // so an event that has been disputed once has two, and which is current is the question.
  // The pack itself is not rendered here at all; the JSON is the signed artefact and the
  // PDF is its rendering, and a third unsigned HTML version would become the one people
  // cite.
  { id: "console.events.proof", home: "console", path: "events/:id/proof", title: "Proof Packs", slice: "V22", kind: "collection", params: { id: DEMO_EVENT } },
  // V23. PAY, at `pay` beside the timesheets it prices.
  //
  // A `collection`: the thing on the screen is the list of priced shifts, and the
  // breakdown expands in place rather than being its own route. `13-…` §3 makes the
  // breakdown the point — a total with no working cannot be defended — and a separate
  // route for it would put the answer one click further from the question.
  { id: "console.events.pay", home: "console", path: "events/:id/pay", title: "Pay", slice: "V23", kind: "collection", params: { id: DEMO_EVENT } },
  // V26. WHO TO OFFER A POST TO, at `match` beside the board that acts on it.
  //
  // A `collection`: the thing on the screen is a list of people, which is the one
  // shape where `over-capacity` means anything — and a bench of eighty ranked
  // against one post is the state where a screen either reads as a decision aid or
  // as a wall. It is per EVENT rather than per requirement because a scheduler
  // fills posts in a sitting and a route per post would be forty routes; the post
  // is a control on the screen.
  // V32. INVOICING AND MARGIN, at `invoicing` beside the pay it is the other
  // side of. A `collection`: the thing on the screen is the list of posts with a
  // price against each, and `over-capacity` means something here for the reason
  // it does on the pay screen — a forty-post stadium turnaround is where a
  // per-row margin either reads as a decision aid or as a wall.
  //
  // PER EVENT AND NOT PER ORGANISATION, because the question a person asks is
  // "are we making anything on Saturday", not "what is our margin". The payroll
  // formats panel is org-wide and sits here anyway; see the ledger, where a
  // dedicated payroll screen is recorded as deferred rather than forgotten.
  { id: "console.events.invoicing", home: "console", path: "events/:id/invoicing", title: "Invoicing and margin", slice: "V32", kind: "collection", params: { id: DEMO_EVENT } },
  { id: "console.events.match", home: "console", path: "events/:id/match", title: "Who to offer this to", slice: "V26", kind: "collection", params: { id: DEMO_EVENT } },
  // V27. THE CASCADE, per EVENT for V26's reason: a scheduler fills posts in a
  // sitting, and a route per post would be forty routes. The post is a control on
  // the screen.
  { id: "console.events.offers", home: "console", path: "events/:id/offers", title: "Offers and reserves", slice: "V27", kind: "collection", params: { id: DEMO_EVENT } },
  // V28. WHAT THE ABSENCE LADDER RAISED, per EVENT for the same reason: lateness
  // is a thing a control room watches across a whole event, and a route per post
  // would be forty routes nobody opens.
  { id: "console.events.attendance", home: "console", path: "events/:id/attendance", title: "Late and absent", slice: "V28", kind: "collection", params: { id: DEMO_EVENT } },
  // Sweep §4. WHAT WENT WRONG ON SITE, per event: recorded once, closed once.
  { id: "console.events.incidents", home: "console", path: "events/:id/incidents", title: "Incidents", slice: "Sweep §4", kind: "collection", params: { id: DEMO_EVENT } },
  // Sweep §4. THE H&S RISK ASSESSMENT, per event and per post: drafted, signed off once, versioned.
  { id: "console.events.risk-assessment", home: "console", path: "events/:id/risk-assessment", title: "Risk assessment", slice: "Sweep §4", kind: "collection", params: { id: DEMO_EVENT } },
  // Sweep §4. WHAT THE CREW WAS TOLD, and who opened it; the reminders that are due.
  { id: "console.events.messages", home: "console", path: "events/:id/messages", title: "Messages to the crew", slice: "Sweep §4", kind: "collection", params: { id: DEMO_EVENT } },
  { id: "console.events.expenses", home: "console", path: "events/:id/expenses", title: "Expenses", slice: "Sweep §4", kind: "collection", params: { id: DEMO_EVENT } },
  // V28. THE APPEAL QUEUE. Under `people` and not under an event, because an
  // appeal is about a PERSON's record rather than about one night's work — and
  // because the person who decides them is looking at a queue, not at a venue.
  { id: "console.people.appeals", home: "console", path: "people/appeals", title: "Appeals", slice: "V28", kind: "collection" },
  { id: "console.people.applicants", home: "console", path: "people/applicants", title: "Applicants", slice: "Sweep §4", kind: "collection" },
  // V29. WHAT IS ABOUT TO GO SHORT, per EVENT and never per post.
  //
  // A `collection`: the thing on the screen is the list of posts ranked worst
  // first, which is what makes `over-capacity` mean something — forty posts on a
  // stadium turnaround is the state where a per-row explanation either reads as a
  // decision aid or as a wall. It is per event because the question a scheduler
  // asks on Tuesday is "what is going to fail on Saturday", not "how is post
  // seventeen doing", and a route per post would be forty routes nobody opens.
  { id: "console.events.risk", home: "console", path: "events/:id/risk", title: "What is about to go short", slice: "V29", kind: "collection", params: { id: DEMO_EVENT } },
  // V25. THE DELIVERY LEDGER AND THE DRAIN.
  //
  // A `collection`: the thing on the screen is the list of deliveries, and the
  // drain is one button above it rather than its own route — a route for an act
  // with no page of its own would be a destination nobody can look at.
  // V30. THE COMPLIANCE CENTRE. Not under an event and not under a person: the
  // register describes the SOFTWARE, and hanging it off one night's work would
  // make it read as a property of that night. A `collection` because the thing on
  // the screen is three lists, and the free-text inventory is sixty-six rows
  // today — which is what makes `over-capacity` mean something here rather than
  // being a state invented to satisfy the arithmetic.
  // `compliance-centre` AND NOT `compliance`, FOR THE REASON `console.workers`
  // is not `worker`: `compliance` is an `app.org_role` id, and V5's guard reads
  // two bare role ids in one file as a vocabulary in the browser — which is
  // absolute rule 4's second source of truth. This file already carried
  // `worker` as a surface, so adding the bare word made it two and failed nine
  // inherited children. The roadmap calls this screen the compliance centre, so
  // the longer URL is also the more accurate one.
  { id: "console.compliance", home: "console", path: "compliance-centre", title: "Compliance", slice: "V30", kind: "collection" },
  // V33. THE DELIVERY SCORECARD, ORG-WIDE AND NOT PER EVENT. The question is
  // "how are we delivering", which is a question about a quarter and a client
  // rather than about Saturday — V32's invoicing screen took the other answer for
  // the opposite reason and the ledger records both. `analytics` is not an
  // `app.org_role` and not a permission id; V30's screen collided a path with a
  // role id and V5 reads this file as a vocabulary in the browser.
  { id: "console.analytics", home: "console", path: "analytics", title: "Delivery scorecard", slice: "V33", kind: "collection" },

  // V34. OFFLINE CAPTURE AND IMPORT, on one screen because they are the same
  // question asked twice: what came in from somewhere that is not this product,
  // and what did we make of it. `sync` is not an `app.org_role` and not a
  // permission id — V30 shipped a path that WAS a role and V5 reads this file as
  // a vocabulary in the browser.
  { id: "console.sync", home: "console", path: "sync", title: "Offline sync and import", slice: "V34", kind: "collection" },
  // V31. THE PRIVACY CENTRE. `privacy-centre` for `compliance-centre`'s reason —
  // `privacy` is not an org_role id today, measured rather than assumed, but the
  // convention in this file is that a console surface never takes a bare
  // vocabulary word, and following it costs a hyphen.
  { id: "console.privacy", home: "console", path: "privacy-centre", title: "Privacy", slice: "V31", kind: "collection" },
  // ★ RUN 76. HELP, AND IT IS IN THE MENU RATHER THAN BEHIND A "?" IN A CORNER.
  //
  // The one question this screen exists for is "why can I not see that", which
  // is the question an RBAC product generates and never answers: the menu hides
  // an entry when the session does not hold its permission, correctly and
  // silently, so a colleague says "it is under Clients" and the person looking
  // has no Clients, no error, and nothing to search for.
  //
  // AN `overview` AND NOT A `notice`, which decides the gallery states it owes.
  // `empty` for an overview is "a session whose role grants nothing" — and that
  // is not a degraded state here, it is the state this screen is MOST useful in.
  { id: "console.help", home: "console", path: "help", title: "Help", slice: "V4", kind: "overview" },
  // Tour T-22. The language and "sign out everywhere", moved out of the header.
  { id: "console.account", home: "console", path: "account", title: "Your account", slice: "Tour", kind: "form" },
  // SWEEP S20. The organisation's own name, clock and currency.
  { id: "console.settings", home: "console", path: "settings", title: "Organisation", slice: "V5", kind: "form" },
  { id: "console.settings.api", home: "console", path: "settings/api", title: "API and webhooks", slice: "Sweep §4", kind: "collection" },
  { id: "console.notifications", home: "console", path: "notifications", title: "Notifications", slice: "V25", kind: "collection" },
  { id: "console.not-found", home: "console", path: "*", title: "Not found in the console", slice: "V4", kind: "notice" },

  // ---- client portal. V24 fills a surface that has been a shell since V4 -----
  //
  // `slice` MOVES TO V24 ON THE FRONT PAGE for the reason it moved to V14 on the
  // crew screen and to V16 on the worker's: the entry read "portal · V4" while
  // showing V24's programme, which sends a reader to the wrong slice's ledger.
  // The id does not move, so no nav entry, no link and no gallery cell had to.
  //
  // `events/:id` HAS NO LITERAL SIBLING AT ITS OWN DEPTH, so the console's
  // `events/new` hazard does not arise here — a client creates nothing.
  { id: "portal.home", home: "portal", path: "", title: "Programme", slice: "V24", kind: "collection" },
  { id: "portal.event", home: "portal", path: "events/:id", title: "Event readiness", slice: "V24", kind: "overview", params: { id: DEMO_EVENT } },
  // S1 PROJECTED. The SAME component with the chrome dropped — 18-…-§7.2's "one
  // CSS state, not a second application". It is a screen in this table rather
  // than a query parameter for one reason: the gallery renders screens, and a
  // wall mode nobody can look at is a wall mode nobody has looked at.
  { id: "portal.event.wall", home: "portal", path: "events/:id/wall", title: "Readiness on a wall", slice: "V24", kind: "overview", params: { id: DEMO_EVENT } },
  { id: "portal.event.roster", home: "portal", path: "events/:id/roster", title: "Roster", slice: "V24", kind: "collection", params: { id: DEMO_EVENT } },
  { id: "portal.event.changes", home: "portal", path: "events/:id/changes", title: "What changed", slice: "V24", kind: "collection", params: { id: DEMO_EVENT } },
  { id: "portal.event.approvals", home: "portal", path: "events/:id/approvals", title: "Approvals", slice: "V24", kind: "collection", params: { id: DEMO_EVENT } },
  { id: "portal.event.documents", home: "portal", path: "events/:id/documents", title: "Documents", slice: "V24", kind: "collection", params: { id: DEMO_EVENT } },
  // D12. A SIBLING OF THE PROGRAMME, NOT OF AN EVENT. The question "is this
  // person free on the 14th" is asked BEFORE there is an event to hang it on, so
  // filing it under events/:id would put it behind the thing it exists to precede.
  { id: "portal.workers", home: "portal", path: "workers", title: "Who is free?", slice: "V37", kind: "collection" },
  // V25. S7, DEFERRED HERE FROM V24 BY DECISION (MORNING-2026-08-15.md §2.33).
  //
  // A `form`: what a client does here is set things, and `REQUIRED_STATES` asks a
  // form for populated and partial. `partial` is the honest one — the settings
  // read while the vocabulary of channels has not — and it is exactly the state
  // where a screen is tempted to invent a default switch.
  { id: "portal.account", home: "portal", path: "account", title: "Your account", slice: "V25", kind: "form" },
  { id: "portal.not-found", home: "portal", path: "*", title: "Not found in the portal", slice: "V4", kind: "notice" },

  // ---- worker app. EVERY ENTRY HERE COSTS DOUBLE IN THE GALLERY -------------
  // §8: the worker chooses light, dark or follow-the-device, so every worker
  // screen registers in BOTH themes, in every state. That doubling is the
  // stated price of the theme decision and is enforced by registry.test.ts.
  // `slice` MOVES TO V16 BECAUSE V16 OWNS IT NOW — run 21 did the same when V14
  // took over the crew screen. It read "worker · V4" in the gallery while showing
  // V16's list, which sends a reader to the wrong slice's ledger entry.
  // V17a. THE PLATFORM REVIEW DESK, on the CONSOLE surface rather than a fifth one.
  // A new `data-surface` needs its own contrast-tested token set and
  // verify-design-parity asserts the four existing sets are complete — a slice of work
  // for one screen, and the Control Room palette is the right register for an internal
  // operations desk anyway. Run 31 records the decision.
  //
  // ONLY PLATFORM STAFF SEE IT. `isEligible` admits them to the console shell for this
  // reason alone, and the desk's own routes are refused twice below the browser.
  { id: "console.platform-reviews", home: "console", path: "platform/reviews", title: "Identity reviews", slice: "V17a", kind: "collection" },
  { id: "worker.home", home: "worker", path: "", title: "My shifts", slice: "V16", kind: "overview" },
  { id: "worker.availability", home: "worker", path: "availability", title: "When I can work", slice: "V12", kind: "collection" },
  // V16. THE LIST IS `worker.home` AND NOT A NEW SCREEN — the worker app's front
  // page was already "My shifts" as a V4 stub, and V16 fills it in. Adding a
  // `worker.shifts` beside it would mean two front pages, one of them a stub
  // nobody removed.
  {
    id: "worker.shift",
    home: "worker",
    path: "shifts/:id",
    title: "This shift",
    slice: "V16",
    kind: "record",
    params: { id: DEMO_SHIFT },
  },
  // V28. THE ROUTE A WORKER CAN ACTUALLY REACH.
  //
  // An `overview` rather than a `collection`: the subject is one number and the
  // sentences that produced it, and the lists below it are what a person does
  // about that number rather than the thing the page is.
  //
  // IT IS ON THE WORKER SURFACE AND IN THE WORKER NAV. A human-review route the
  // subject cannot find is the rubber stamp Directive 2024/2831 forbids, and a
  // route reachable only by typing a URL is one nobody finds.
  { id: "worker.record", home: "worker", path: "record", title: "My record", slice: "V28", kind: "overview" },
  // SWEEP §4 / 0179. Languages and the emergency contact, kept up to date by the person.
  { id: "worker.profile", home: "worker", path: "profile", title: "About me", slice: "Sweep §4", kind: "form" },
  // SWEEP §4 / 0190. Money spent on a shift, claimed back, and what was decided.
  { id: "worker.expenses", home: "worker", path: "expenses", title: "My expenses", slice: "Sweep §4", kind: "collection" },
  // SWEEP S8. Where the identity provider sends a worker back; it records the result.
  { id: "worker.verification-return", home: "worker", path: "verification/return", title: "Identity check", slice: "V16a", kind: "notice" },
  { id: "worker.not-found", home: "worker", path: "*", title: "Not found in the worker app", slice: "V4", kind: "notice" },
] as const satisfies readonly Screen[];

export type ScreenId = (typeof SCREENS)[number]["id"];

/**
 * A screen NARROWED to a real id.
 *
 * `Screen` widens `id` to `string`, which is right for the table's declaration
 * and wrong for anything that then looks the screen up in a
 * `Record<ScreenId, …>` — and there are now two such records, the element map
 * and the gallery registry. Passing a `Screen` around instead of this is how
 * the exhaustiveness those records buy gets thrown away at the first function
 * boundary.
 */
export type KnownScreen = (typeof SCREENS)[number];

/** Lookup, and it throws rather than returning undefined — see screens.test.ts. */
export function screenById(id: ScreenId): Screen {
  const found = SCREENS.find((s) => s.id === id);
  if (!found) throw new Error(`no screen with id ${id}`);
  return found;
}

/**
 * The concrete URL the gallery renders a screen at.
 *
 * `*` becomes a path that MATCHES the catch-all rather than the literal star:
 * rendering a not-found screen at the URL "/console/*" would exercise a route
 * nobody can reach, and the point of registering it is to see what a real
 * mistyped link produces.
 */
export function demoUrlFor(screen: Screen, prefix: Record<Surface, string>): string {
  const base = screen.home === "public" ? "" : prefix[screen.home];
  let path = screen.path;
  if (path === "*") path = "no-such-page";
  for (const [name, value] of Object.entries(screen.params ?? {})) {
    path = path.replace(`:${name}`, value);
  }
  // A public screen's `path` is already absolute — EXCEPT the catch-all, whose
  // substitution above produced a bare segment. Returning it unchanged gave a
  // relative URL, which MemoryRouter resolves against nothing and renders as a
  // blank cell. Found by the test rather than by looking, which is the only way
  // it would have been found: the cell renders, it just renders nothing.
  const search = screen.search ?? "";
  if (screen.home === "public") return (path === "" ? "/" : path.startsWith("/") ? path : `/${path}`) + search;
  return (path === "" ? base : `${base}/${path}`) + search;
}

/**
 * The ROUTE PATTERN the gallery must match a cell against — `:id` intact.
 *
 * THIS EXISTS BECAUSE THE GALLERY DID NOT HAVE IT, AND THE CONSEQUENCE WAS
 * INVISIBLE FOR EIGHT SLICES. `Gallery.tsx` passed `demoUrlFor`'s output — the
 * CONCRETE url, with the id already substituted — as the `<Route path>`. A
 * route path containing no `:param` segment yields no params, so every
 * parameterised screen rendered with `useParams().id` undefined, asked the
 * gallery for `/v1/events//requirements`, and got a "no fixture" panel.
 *
 * Seven screens, every state, every run — and nothing failed, because the
 * registry is checked structurally and a catch-all `<Route path="*">` sitting
 * beneath meant the screen still rendered. It was found by opening the gallery
 * and reading it, which is the only way it could have been found.
 *
 * It lives beside `demoUrlFor` deliberately: the two answer the same question
 * about the same screen and must not drift, and screens.test.ts asserts that
 * substituting one gives the other.
 */
export function demoPatternFor(screen: Screen, prefix: Record<Surface, string>): string {
  if (screen.home === "public") return screen.path;
  const base = prefix[screen.home];
  return screen.path === "" ? base : `${base}/${screen.path}`;
}

/** Every `:param` this path needs. Used to prove `params` is complete. */
export function parametersIn(path: string): string[] {
  return path
    .split("/")
    .filter((segment) => segment.startsWith(":"))
    .map((segment) => segment.slice(1));
}
